import { useRef } from 'react';

import { LoggerHelper, ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { FingerprintHelper } from '@bcp-channels-sharedcomponents-reactjs/utils';
import { InputFieldModels } from '@bcp-nextgen-dx-component-factory/design-system';
import { AppHelper } from '@helpers';
import { useLoader, useReactiveForm, useTranslation } from '@hooks';
import { useLoginStore, useSearchParamsStore, useServicesStore } from '@store';
import { endpoints } from '@types';

import {
	ALLOWED_CHARS_REGEX,
	DIGIT_REGEX,
	LOWER_CASE_REGEX,
	PasswordFormValues,
	PasswordValidations,
	resources,
	UPPER_CASE_REGEX,
} from './passwordForm.types';

export const usePasswordFormHelper = () => {
	const { t } = useTranslation();
	const globalLoader = useLoader('global');

	const language = useSearchParamsStore((state) => state.language);
	const accountLogin = useServicesStore((state) => state.accountLogin);
	const accountChallenge = useServicesStore((state) => state.accountChallenge);
	const oldPassword = useLoginStore((state) => state.form?.password);
	const accountUserCredentialsErrorMessage = useServicesStore((state) => state.accountUserCredentials?.errorMessage);

	const passwordRef = useRef<InputFieldModels.InputFieldRef>(null);
	const confirmPasswordRef = useRef<InputFieldModels.InputFieldRef>(null);

	const { getCFPInputProps, submitForm, setErrors, isValid } = useReactiveForm({
		name: 'passwordForm',
		validate: (values: PasswordFormValues) => {
			const { password, confirmPassword } = values;

			/* custom validation:
			 - confirmPassword should be equal to password */
			let confirmPasswordErrorMessage = '';
			if (password && confirmPassword && password !== confirmPassword) {
				confirmPasswordErrorMessage = t(resources.validations.passwordMismatch);
			}

			return {
				confirmPassword: confirmPasswordErrorMessage,
			};
		},
		onSubmit: (values) => {
			handleFormSubmit(values);
		},
		options: {
			validations: {
				allowErrorsOnLaunch: false,
			},
		},
	});

	const sanitizeAlphanumeric = (inputRef: React.RefObject<InputFieldModels.InputFieldRef | null>) => {
		const el = inputRef.current;
		const elValue = el?.getValue();
		const clean = elValue?.replace(/[^A-Za-z0-9]/g, '');

		// Only touch the DOM if needed (prevents cursor jumps)
		if (el && elValue !== clean) {
			el.setNativeValue(clean || '');
		}
	};

	const handlePasswordChange = (value: string) => {
		getCFPInputProps('password').onChange?.(value);

		confirmPasswordRef.current?.updateValue('');
		getCFPInputProps('confirmPassword').onChange?.('');

		setErrors({});
	};

	const handleConfirmPasswordChange = (value: string) => {
		getCFPInputProps('confirmPassword').onChange?.(value);
	};

	const handleFormSubmit = async (values: PasswordFormValues) => {
		LoggerHelper.debug('App', 'PasswordForm > handleFormSubmit:', { values });

		globalLoader.show();

		const {
			userAlias,
			userAliasTypeId,
			returnUrl,
			sdcSessionId,
			sdcBusinessSessionId,
			geo,
			targetSystem,
			targetInfrastructure,
		} = accountLogin || {};
		const { updateCredentialType, certValid, certValidUntil } = accountChallenge || {};

		const fingerprint = await FingerprintHelper.getFingerprint();

		AppHelper.submitForm(endpoints.accountUserCredentials, {
			language: language?.toUpperCase(),
			userAlias,
			userAliasTypeId: userAliasTypeId?.toString(),
			returnUrl,
			sdcSessionId,
			sdcBusinessSessionId,
			geo,
			targetSystem,
			targetInfrastructure,
			fingerprint,
			/* checks both null and undefined */
			...(certValid != null && { certValid: certValid.toString() }),
			...(certValidUntil != null && { certValidUntil }),
			challengeType: updateCredentialType?.toString(),
			oldCredential: oldPassword,
			newCredential: values.password,
			newConfirmCredential: values.confirmPassword,
		});
	};

	const validatePassword = (value?: string): PasswordValidations => {
		const errorMessage = ' ';

		const required = ValidationHelper.validateRequired(t(resources.validations.required))(value);
		const allowedChars = ValidationHelper.validateRegex(
			ALLOWED_CHARS_REGEX,
			t(resources.validations.allowedChars)
		)(value);
		const mandatoryLowerCase = ValidationHelper.validateRegex(LOWER_CASE_REGEX, errorMessage)(value);
		const mandatoryUpperCase = ValidationHelper.validateRegex(UPPER_CASE_REGEX, errorMessage)(value);
		const mandatoryDigit = ValidationHelper.validateRegex(DIGIT_REGEX, errorMessage)(value);
		const requiredLength =
			ValidationHelper.validateMinLength(8, errorMessage)(value) ||
			ValidationHelper.validateMaxLength(8, errorMessage)(value);

		return {
			required,
			allowedChars,
			mandatoryLowerCase,
			mandatoryUpperCase,
			mandatoryDigit,
			requiredLength,
		};
	};

	const validatePasswordValid = (passwordValidations: PasswordValidations) => {
		return !Object.values(passwordValidations).some((valid) => !!valid?.message);
	};

	const passwordValidations = validatePassword(getCFPInputProps('password').value);
	const isPasswordValid = validatePasswordValid(passwordValidations);
	const isFormValid = isPasswordValid && getCFPInputProps('confirmPassword').value && isValid;

	return {
		passwordRef,
		confirmPasswordRef,
		isFormValid,
		passwordValidations,
		isPasswordValid,
		accountUserCredentialsErrorMessage,
		t,
		sanitizeAlphanumeric,
		handlePasswordChange,
		handleConfirmPasswordChange,
		getCFPInputProps,
		submitForm,
	};
};
